import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useDeepCompareEffect } from 'ahooks';
import { noop } from 'lodash-es';

import { appConfig } from '@ll-platform/frontend/config/app.config';

export enum FeatureFlagCustomContextEnum {
  Project = 'project',
}

type CustomContextValue = {
  key: string;
} & Record<string, unknown>;

export type FeatureFlagCustomContextType = {
  contexts: Map<FeatureFlagCustomContextEnum, CustomContextValue>;
  setContext: (
    name: FeatureFlagCustomContextEnum,
    value: CustomContextValue | null,
  ) => void;
};

const defaultContext: FeatureFlagCustomContextType = {
  contexts: new Map(),
  setContext: noop,
};

const FeatureFlagCustomContext =
  createContext<FeatureFlagCustomContextType>(defaultContext);
FeatureFlagCustomContext.displayName = 'FeatureFlagCustomContext';

export const FeatureFlagCustomContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [contexts, setContexts] = useState<
    FeatureFlagCustomContextType['contexts']
  >(defaultContext.contexts);

  const setContext = useCallback(
    (name: FeatureFlagCustomContextEnum, value: CustomContextValue | null) => {
      setContexts((current) => {
        if (appConfig.REACT_APP_FEATURE_FLAGS_DEBUG) {
          console.debug('[FeatureFlags] Set Custom Context', name, value);
        }
        const newContexts = new Map(current);
        if (value) {
          newContexts.set(name, value);
        } else {
          newContexts.delete(name);
        }

        return newContexts;
      });
    },
    [],
  );

  const value = useMemo(
    () => ({
      contexts,
      setContext,
    }),
    [contexts, setContext],
  );

  return (
    <FeatureFlagCustomContext.Provider value={value}>
      {children}
    </FeatureFlagCustomContext.Provider>
  );
};

export const useFeatureFlagCustomContext = () => {
  return useContext(FeatureFlagCustomContext);
};

// To prevent race conditions, please only register this component once per context name
type GlobalSetFeatureFlagCustomContextProps = PropsWithChildren & {
  name: FeatureFlagCustomContextEnum;
  value: CustomContextValue | null;
};

export function GlobalSetFeatureFlagCustomContext({
  children,
  name,
  value,
}: GlobalSetFeatureFlagCustomContextProps) {
  const { setContext } = useFeatureFlagCustomContext();
  useDeepCompareEffect(() => {
    setContext(name, value);
  }, [name, value, setContext]);

  useEffect(() => {
    return () => {
      setContext(name, null);
    };
  }, [name, setContext]);

  return children;
}
